import { Box, Container, makeStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';
import { ContentDescription } from 'components/structures/general';
import { useParallax } from 'hooks';
import { ISrcSet, row1Images, row2Images, row3Images } from 'images/retail-energy/partners';
import React, { FC } from 'react';
import { animated } from 'react-spring';

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    padding: theme.spacing(10, 0),
    overflow: 'hidden',
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(20, 0),
    },
  },
  text: {
    textAlign: 'center',
    maxWidth: 850,
    margin: '0 auto',
  },
  title: {
    marginBottom: theme.spacing(6),
    [theme.breakpoints.up('md')]: {
      marginBottom: theme.spacing(5),
    },
  },
  description: {
    marginBottom: theme.spacing(7),
  },
  logoBox: {
    minWidth: 220,
    minHeight: 70,
    maxWidth: '100%',
    maxHeight: '100%',
    borderRadius: theme.spacing(4),
    backgroundColor: theme.palette.common.white,
    [theme.breakpoints.up('md')]: {
      minWidth: 410,
      minHeight: 130,
    },
  },
  partnerImage: {
    width: '100%',
    height: 'auto',
    maxHeight: 40,
    maxWidth: 200,
    userSelect: 'none',
    objectFit: 'contain',
    [theme.breakpoints.up('md')]: {
      maxHeight: 60,
      maxWidth: 300,
    },
  },
}));

const repeat = (arr: Array<unknown>, times = 5) => {
  const newArray = [];
  for (let i = 0; i < times; i++) {
    newArray.push(...arr);
  }

  return newArray;
};

const SuppliersList: FC = () => {
  const classes = useStyles();

  return (
    <Box className={classes.wrapper}>
      <Container>
        <ContentDescription
          title={{
            text: 'A comprehensive list of Energy Suppliers',
            variant: 'h1',
            className: clsx(classes.text, classes.title),
          }}
          text={{
            className: clsx(classes.text, classes.description),
            text: "Energy5 provides information on available Energy Suppliers per gas and electric companies' current pricing policy. With an extensive network of Energy Suppliers, you can always count on Energy5 to get you the best energy plans in existence. Our platform collaborates with trusted, time-tested, and most influential Energy Suppliers in the entire Energy Industry.",
          }}
        />
        <ImageSlider images={repeat(row1Images)} />
        <ImageSlider images={repeat(row2Images)} reverse />
        <ImageSlider images={repeat(row3Images)} />
      </Container>
    </Box>
  );
};

interface ImageSliderProps {
  images: ISrcSet[];
  reverse?: boolean;
}

const ImageSlider: FC<ImageSliderProps> = ({ images, reverse = false }) => {
  const classes = useStyles();
  const { ParallaxProvider, transform } = useParallax('X');
  const style = { transform: transform(reverse ? 0.5 : -0.5) };
  return (
    <ParallaxProvider>
      <animated.div style={style}>
        <Box display="flex" flexWrap="no-wrap" ml={reverse ? -400 : -200}>
          {images.map(({ x1, x2 }, index) => (
            <Box key={x1 + index} className={classes.logoBox} m={3} display="flex" justifyContent="center" alignItems="center">
              <img className={classes.partnerImage} src={x1} alt={x1 + x2} srcSet={`${x1} 500w, ${x2} 1080w`} />
            </Box>
          ))}
        </Box>
      </animated.div>
    </ParallaxProvider>
  );
};

export default SuppliersList;
